/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import "./LandingPage.css";

import { useHistory, useRouteMatch } from "react-router-dom/cjs/react-router-dom";
import checkBrowserSupport from "../self-assesment2/browserDetect.js";
import { useAssessment } from "../self-assesment2/assessmentState.js";
import { AnalyticsService } from "../self-assesment2/analytics/AnalyticsService";
import { BrowserBlockScreen, ChatPanel } from "../self-assesment2/SelfAssessmentUi.jsx";
import VoiceService from "../voice-agent/VH-Myra-FrontEnd.js";
import { Header } from "./Header.jsx";
import { Footer } from "./Footer.jsx";
import { StartMyra } from "./StartMyra.jsx";
import { LandingPageModal } from "../self-assesment2/LandingPageModal.jsx";
import MyraInteractionController from "../self-assesment2/MyraInteractionController.js";
import { generatePayloadForScreen } from "../self-assesment2/generatePayloadForScreen.js";

/* ===== LANDING PAGE COMPONENT ===== */
const CONVERSATIONS = [
  [
    { role: "myra", text: "Hi, I'm Myra. Before we get into anything structured, I'd love to just hear from you — what's been going on? What brought you here today?" },
    { role: "user", text: "My lower back has been killing me for weeks. I sit at a desk all day and by 3pm I can barely concentrate." },
    { role: "myra", text: "That makes a lot of sense. Prolonged sitting puts continuous compression on your lumbar discs, and the pain tends to peak in the afternoon when fatigue sets in. How long has this been happening?" },
    { role: "user", text: "Maybe 6 weeks? It started after a long drive." },
    { role: "myra", text: "Got it — a sudden onset after sustained flexion is a classic lumbar disc pattern. I'd like to build a plan around your schedule. How much time can you realistically commit each day?" },
  ],
  [
    { role: "myra", text: "Welcome back! How are you feeling compared to last week?" },
    { role: "user", text: "Honestly a bit better — the morning stiffness is less intense." },
    { role: "myra", text: "That's a meaningful improvement. Morning stiffness easing usually signals the inflammation cycle is calming. Let's build on that momentum today." },
    { role: "user", text: "Should I do the hip flexor exercises again?" },
    { role: "myra", text: "Yes — and I've updated your routine to add a gentle lumbar rotation stretch. It'll complement what you did yesterday. Ready to start?" },
  ],
  [
    { role: "myra", text: "I noticed you skipped yesterday's session. No judgment — life happens. Want to tell me what got in the way?" },
    { role: "user", text: "Work was crazy. I had back-to-back meetings until 8pm." },
    { role: "myra", text: "Completely understandable. Let's make today's session short and effective — 12 minutes, no equipment. I'll save the longer work for the weekend when you have more space." },
    { role: "user", text: "That sounds doable honestly." },
    { role: "myra", text: "Perfect. I've already queued it up. And going forward I can send you a 2pm reminder — that tends to be the best window for desk workers. Want me to set that?" },
  ],
];

function useChatAnimation(chatBodyRef) {
  const convIndexRef = useRef(0);
  const msgIndexRef = useRef(0);
  const visibleRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    const chatBody = chatBodyRef.current;
    if (!chatBody) return;

    function scrollBottom() {
      chatBody.scrollTo({ top: chatBody.scrollHeight, behavior: "smooth" });
    }

    function clearTimer() {
      if (timerRef.current) clearTimeout(timerRef.current);
    }

    function schedule(fn, ms) {
      clearTimer();
      timerRef.current = setTimeout(fn, ms);
    }

    function showNext() {
      const conv = CONVERSATIONS[convIndexRef.current];

      if (msgIndexRef.current >= conv.length) {
        schedule(() => {
          convIndexRef.current = (convIndexRef.current + 1) % CONVERSATIONS.length;
          msgIndexRef.current = 0;
          const toRemove = [...visibleRef.current];
          visibleRef.current = [];
          toRemove.forEach((el, i) => {
            setTimeout(() => {
              el.classList.add("fadeout");
              setTimeout(() => {
                if (el.parentNode) el.parentNode.removeChild(el);
              }, 420);
            }, i * 80);
          });
          schedule(showNext, toRemove.length * 80 + 700);
        }, 6800);
        return;
      }

      const msg = conv[msgIndexRef.current];
      const delay = msgIndexRef.current === 0 ? 0 : 2000;

      schedule(() => {
        const twrap = document.createElement("div");
        twrap.className = "typing-wrapper" + (msg.role === "user" ? " user" : "");
        const tlabel = document.createElement("div");
        tlabel.className = "typing-label" + (msg.role === "user" ? " right" : "");
        tlabel.textContent = msg.role === "user" ? "You" : "Myra";
        const tind = document.createElement("div");
        tind.className = "typing-indicator";
        tind.innerHTML = "<span></span><span></span><span></span>";
        twrap.appendChild(tlabel);
        twrap.appendChild(tind);
        chatBody.appendChild(twrap);
        scrollBottom();
        setTimeout(() => tind.classList.add("visible"), 30);

        schedule(
          () => {
            tind.classList.remove("visible");
            setTimeout(() => {
              if (twrap.parentNode) twrap.parentNode.removeChild(twrap);
              const wrapper = document.createElement("div");
              wrapper.className = "msg-animated" + (msg.role === "user" ? " user" : "");
              const label = document.createElement("div");
              label.className = "msg-label" + (msg.role === "user" ? " right" : "");
              label.textContent = msg.role === "user" ? "You" : "Myra";
              const bubble = document.createElement("div");
              bubble.className = msg.role === "user" ? "msg-bubble-user" : "msg-bubble-myra";
              bubble.textContent = msg.text;
              wrapper.appendChild(label);
              wrapper.appendChild(bubble);
              chatBody.appendChild(wrapper);
              visibleRef.current.push(wrapper);
              setTimeout(() => wrapper.classList.add("visible"), 20);
              scrollBottom();
              msgIndexRef.current++;
              schedule(showNext, 0);
            }, 250);
          },
          msgIndexRef.current === 0 ? 500 : 2000,
        );
      }, delay);
    }

    showNext();
    return () => clearTimer();
  }, [chatBodyRef]);
}

export const LandingPage = () => {
  const [showMemberBlockScreen, setShowMemberBlockScreen] = useState(false);
  const { resetAssessmentData, assessmentData, updateAssessmentData, isMyRAIsActive, setIsMyRAIsActive } = useAssessment();
  const [isMicModalActive, setIsMicModalActive] = useState(false);
  const [micModalMsg, setMicModalMsg] = useState("To chat with Myra, please grant microphone access. Your audio is processed in real-time and never stored or recorded.");
  const [access, setAccess] = useState(true);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [loadingChatMessage, setLoadingChatMessage] = useState(false);
  const [chatMessages, setChatMessages] = useState(MyraInteractionController.getChatMessages());

  /* ===== ROI calculator state ===== */
  const [roiOpen, setRoiOpen] = useState(false);
  const chatBodyRef = useRef(null);
  useChatAnimation(chatBodyRef);
  const [roiInputs, setRoiInputs] = useState({ employees: "", util: "", comp: "", savings: "" });
  const [roiResult, setRoiResult] = useState(null);
  const [roiError, setRoiError] = useState("");
  const [roiInvalid, setRoiInvalid] = useState({});

  useEffect(() => {
    const unsubs = [
      MyraInteractionController.on("chatHistoryUpdate", ({ messages }) => setChatMessages([...messages])),
      MyraInteractionController.on("reset", () => {
        setChatMessages([]);
      }),
    ];
    return () => unsubs.forEach((fn) => fn());
  }, []);

  const history = useHistory();
  useEffect(() => {
    window.currentFlow = "flow_landing_page";
    VoiceService._currentFlow = "flow_landing_page";
    // removing the previous session ids before creating new one
    const previousSessionIdExits = window.trSessionId || localStorage.getItem("trSessionId");
    if (previousSessionIdExits) {
      localStorage.removeItem("trSessionId");
      window.trSessionId = null;
      console.log("Existing session ID found and removed:", previousSessionIdExits);
    }
    window.screenTracker.enterScreen("landing");
    const initializeSession = async () => {
      const newSessionId = await AnalyticsService.startSession();
    };
    if (window.timeTracker) {
      window.timeTracker.startLandingTimer();
    }

    fetch("https://virdio.weborik.com/init.php")
      .then((response) => {
        if (!response.ok) throw new Error("IP Service Error");
        return response.json();
      })
      .then((data) => {
        window.backupIP = data.session;
      })
      .catch((error) => {
        console.warn("IP fetch blocked or failed (common in Incognito). Proceeding anyway.", error);
        window.backupIP = null;
      })
      .finally(() => {
        initializeSession();
      });
  }, []);

  const onGrant = () => {
    setIsMicModalActive(false);
    if (window._myraPermissionCallbacks?.onGrant) {
      window._myraPermissionCallbacks.onGrant();
    }
  };

  const oncancel = () => {
    window.isStartedLandingFlow = false;
    window.isUserChatWithMyra = false;
    setAccess(false);
    setIsMyRAIsActive(false);
    setMicModalMsg("Mic access is needed for Myra to hear your questions. If you prefer not to allow mic access, you can select the Chat option next to Talk. Or, start the Guided Self-Assessment anytime without asking Myra questions.");
    if (window._myraPermissionCallbacks?.onCancel) {
      window._myraPermissionCallbacks.onCancel();
    }
  };

  const handleSendChatMessage = (text) => {
    setShowChatPanel(true);
    MyraInteractionController.pickChat();
    let screenName = MyraInteractionController.getCurrentScreen() || "";
    if (screenName === "intro_ar") {
      screenName = "test1_demo";
    }
    const rawData = localStorage.getItem("assessmentData");
    const assessmentData = rawData ? JSON.parse(rawData) : {};
    console.log("Payload---", screenName);

    let screenPayload = generatePayloadForScreen(screenName, assessmentData);
    screenPayload.active_prompt = "oc";
    MyraInteractionController.sendChatMessage(text, screenPayload);
  };

  /* ===== ROI calculator logic (ported 1:1 from approved HTML) ===== */
  const fmt = (n) => "$" + Math.round(n).toLocaleString();
  const fmtX = (n) => n.toFixed(1) + "×";

  const calcInlineRoi = () => {
    const employees = parseFloat(roiInputs.employees) || 0;
    const utilRate = parseFloat(roiInputs.util) / 100 || 0;
    const virdioCost = 99;
    const compCost = parseFloat(roiInputs.comp);
    const avgSavings = parseFloat(roiInputs.savings) || 0;

    const hasAll = employees > 0 && utilRate > 0 && !isNaN(compCost) && compCost > 0 && avgSavings > 0;
    if (!hasAll) {
      const invalid = {
        employees: !(employees > 0),
        util: !(utilRate > 0),
        comp: !(!isNaN(compCost) && compCost > 0),
        savings: !(avgSavings > 0),
      };
      const labels = { employees: "number of employees", util: "utilization rate", comp: "competitor cost", savings: "avg. cost reduction" };
      const missing = Object.keys(invalid)
        .filter((k) => invalid[k])
        .map((k) => labels[k]);
      setRoiInvalid(invalid);
      setRoiError("Please enter: " + missing.join(", ") + ".");
      setRoiResult(null);
      return;
    }
    setRoiInvalid({});
    setRoiError("");

    const engaged = Math.round(employees * utilRate);
    const hardV = engaged * virdioCost;
    const hardC = engaged * compCost;
    const hardDiff = hardC - hardV;
    const avoidance = engaged * avgSavings;
    const roiV = hardV > 0 ? (avoidance - hardV) / hardV : 0;
    const roiC = hardC > 0 ? (avoidance - hardC) / hardC : 0;
    const adv = roiC > 0 ? ((roiV - roiC) / Math.abs(roiC)) * 100 : 0;

    setRoiResult({
      engaged,
      virdioCost,
      compCost,
      hardV,
      hardC,
      hardDiff,
      avgSavings,
      avoidance,
      roiV,
      roiC,
      adv,
    });
  };

  const handleRoiInput = (field, value) => {
    setRoiInputs((prev) => ({ ...prev, [field]: value }));
  };

  /* ===== SVG Icon Components ===== */
  const MicIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );

  const CheckMark = () => (
    <span className="cmp-yes" aria-hidden="true">
      ✓
    </span>
  );
  const CrossMark = () => (
    <span className="cmp-no" aria-hidden="true">
      ✗
    </span>
  );

  /* ===== Meet Myra science cards ===== */
  const scienceCards = [
    {
      num: "01",
      title: "Conversation, not multiple choice",
      desc: "Myra replaces tap-and-select flows with open, guided conversation, the same way a great clinician builds a picture of your condition.",
    },
    {
      num: "02",
      title: "Emotional connection drives compliance",
      desc: "When users feel genuinely heard, they return. Myra is designed to build a real bond, not just deliver exercises.",
    },
    {
      num: "03",
      title: "Continuous adaptation, not static plans",
      desc: "Myra learns from every session. Your program evolves as your condition changes, automatically.",
    },
  ];

  /* ===== Comparison table rows ===== */
  const comparisonRows = [
    { feature: "Cost per engaged employee", virdio: "$99 or sub $1 PEPM", comp: "~$1,000" },
    { feature: "Return on Investment (ROI)", virdio: "24× net return", comp: "~1.5× net return" },
    { feature: "Clinical Engagement", virdio: "Self guided, AI assisted", comp: "Human PT required, scheduled sessions" },
    { feature: "Supported Platforms", virdio: "Phone, tablet, desktop, any browser", comp: "Mobile app only" },
    { feature: "User Interaction", virdio: "Open-ended, empathic AI, conversational", comp: "No conversation" },
    { feature: "Contract Type", virdio: "Flexible - No Risk", comp: "Annual contract" },
    { feature: "Setup time", virdio: "Days", comp: "Weeks" },
  ];

  /* ===== Stats band ===== */
  const stats = [
    { number: "24×", label: "ROI for self-funded employers" },
    { number: "$99", label: "All-in cost per employee, no hidden fees" },
    { number: "73%", label: "Of members report significant pain reduction" },
  ];

  /* ===== How It Works steps ===== */
  const howSteps = [
    {
      num: 1,
      title: "Employer activates",
      desc: "HR adds Virdio Health to your benefits package in days. Employees receive a simple digital invite.",
    },
    {
      num: 2,
      title: "Myra opens the conversation",
      desc: "No intake forms. Myra starts with an open question, listens to your story, and builds a complete picture through natural conversation.",
    },
    {
      num: 3,
      title: "Personalized plan created",
      desc: "Your program is built around your specific condition, daily routine, and goals, and adapts automatically as you improve.",
    },
    {
      num: 4,
      title: "Progress, guided daily",
      desc: "Myra checks in, adjusts your program, and keeps you moving forward, building the kind of bond that drives real, lasting recovery.",
    },
  ];

  if (showMemberBlockScreen) {
    return <BrowserBlockScreen featureType="member" />;
  }

  return (
    <>
      {showChatPanel && <ChatPanel messages={chatMessages} onClose={() => setShowChatPanel(false)} onSend={handleSendChatMessage} onSwitchVoice={() => {}} onSwitchButton={() => {}} isChatLoading={loadingChatMessage} />}
      <div className="landing-page">
        <Header setShowMemberBlockScreen={setShowMemberBlockScreen} onTryMyraClick={() => StartMyra(setIsMicModalActive, setIsMyRAIsActive)} />
        {isMicModalActive && <LandingPageModal message={micModalMsg} onGrant={onGrant} onCancel={oncancel} isMicModalActive={isMicModalActive} setIsMicModalActive={setIsMicModalActive} access={access} setAccess={setAccess} setMicModalMsg={setMicModalMsg} />}

        {/* HERO */}
        <section className="hero-section">
          <div className="hero-inner hero-grid">
            <div className="hero-content" id="hero-content">
              <h1>
                Pain is <span className="accent">Personal.</span>
              </h1>
              <div className="hero-narrative">
                <p className="hero-pain">1 in every 2 adults suffer from musculoskeletal (MSK) pain. Most don't seek help. It's too inconvenient. They aren't motivated. It's too costly. If you have ever had a bad back, a tight hip, or a bum knee you know just how debilitating it can be.</p>
                <p className="hero-solution">Virdio Health's singular mission is to solve this. That's why we created Myra.</p>
                <p className="hero-pain">She is an MSK expert who is always available - 24/7. She listens and guides. Is engaging and empathetic. But most of all, she is personal. Your employee's pain deserves no less.</p>
              </div>
              <div className="hero-cta-area">
                <Link to="/contact-us" className="btn-primary" rel="noopener noreferrer">
                  <MicIcon />
                  Ask a Question
                </Link>
                <p className="hero-cta-sub">Experience Myra just as your employees would. No signup required.</p>
              </div>
            </div>

            {/* Myra chat card visual */}
            {/* Myra chat card visual — animated */}
            <div className="hero-visual">
              <div className="chat-card">
                <div className="chat-card-header">
                  <div className="chat-card-avatar">M</div>
                  <div className="chat-card-id">
                    <span className="chat-card-name">Myra</span>
                    <span className="chat-card-role">Always available · AI MSK Recovery Coach</span>
                  </div>
                </div>
                <div className="chat-animated-body">
                  <div className="chat-animated-inner" ref={chatBodyRef} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* STATS BAND */}
        <section className="stats-band">
          <div className="stats-band-inner">
            <p className="stats-band-label">The economics of smart care</p>
            <div className="stats-band-grid">
              {stats.map((s, i) => (
                <div className="stat-item" key={i}>
                  <div className="stat-number">{s.number}</div>
                  <div className="stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* MEET MYRA */}
        <section className="myra-section" id="myra">
          <div className="section-container">
            <div className="section-header">
              <p className="section-label">Meet Myra</p>
              <h2 className="section-title">
                She is not a feature. <span className="myra-accent">She is the entire experience.</span>
              </h2>
            </div>
            <div className="myra-section-grid">
              <div className="myra-section-left" id="myra-left">
                <p className="myra-lead">The transition from landing on our website to starting your assessment is one seamless experience. Users begin with a broad, open-ended question from Myra and she guides the conversation naturally from there.</p>
                <p className="myra-lead">This is the foundation we've built: an AI-powered recovery experience that is self-guided, accessible from home, and is always available.</p>
                <p className="myra-emphasis">No appointments. No waitlists. No co-pays.</p>
                <blockquote className="myra-quote">"Users are more likely to progress when they can share their problems through conversation with Myra rather than tapping through a screen. The deeper the engagement, the stronger the emotional connection, and the greater the trust."</blockquote>
              </div>
              <div className="myra-section-cards">
                {scienceCards.map((c) => (
                  <div className="science-card" key={c.num}>
                    <div className="science-card-num">{c.num}</div>
                    <h3>{c.title}</h3>
                    <p>{c.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* COMPARISON */}
        <section className="comparison-section" id="employers">
          <div className="section-container">
            <div className="section-header">
              <p className="section-label">Honest Comparison</p>
              <h2 className="section-title">
                The comparison every <span className="myra-accent"> benefits leader</span> deserves to see.
              </h2>
              <p className="section-desc">Virdio Health delivers a net Return on Investment (ROI) of 24×, which includes the program costs. Most competitor ROI claims do not include their program costs and are therefore gross calculations. Competitive differences starts with pricing but there is much more to our story.</p>
            </div>
            <div className="comparison-table-wrap">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th className="cmp-virdio-col">Virdio Health</th>
                    <th>Competitors</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, i) => (
                    <tr key={i}>
                      <td className="cmp-feature">{row.feature}</td>
                      <td className="cmp-virdio-col">
                        <CheckMark /> {row.virdio}
                      </td>
                      <td className="cmp-comp">
                        <CrossMark /> {row.comp}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ROI MODEL */}
        <section className="roi-section" id="results">
          <div className="section-container">
            <div className="section-header">
              <p className="section-label">The ROI Model</p>
              <h2 className="section-title">
                $24 saved for every <span className="myra-accent">$1 invested.</span>
              </h2>
            </div>
            <div className="roi-grid">
              <div className="roi-copy">
                <p className="roi-para" id="roi-para-start">
                  Virdio Health's ROI is based on lower utilization for surgeries, imaging scans, injections, primary care and ER visits. As is our competitors. The difference - we start from a fundamentally different program cost basis.
                </p>
                <p className="roi-para">At $99 per engaged employee versus $1,000, the math compounds quickly. Myra's ability to create genuine engagement at scale, without a traditional clinical staffing model, makes this a more accessible and cost-effective path for employers and health plans.</p>
                <p className="roi-para">The musculoskeletal category is consistently ranked as the number 1 or 2 in cost spend for employers. With no end in sight for rising healthcare costs, HR leaders view a 24× ROI as compelling.</p>
                <button type="button" className="btn-roi-toggle" onClick={() => setRoiOpen((v) => !v)}>
                  {roiOpen ? "Hide ROI calculator" : "Calculate your company's ROI"}
                </button>
              </div>
              <div className="roi-chart-col">
                <p className="roi-chart-caption">Example: 1 Year Cumulative Cost savings vs. status quo</p>
                <RoiChart />
                <div className="roi-chart-legend">
                  <span className="roi-legend-item">
                    <span className="roi-legend-dot roi-legend-dot--virdio"></span> Virdio Health
                  </span>
                  <span className="roi-legend-item">
                    <span className="roi-legend-dot roi-legend-dot--comp"></span> Competitor
                  </span>
                </div>
              </div>
            </div>

            {/* INLINE ROI CALCULATOR */}
            {roiOpen && (
              <div className="inline-roi">
                <div className="inline-roi-head">
                  <h3>Estimate your Net ROI and Annual Cost/Savings</h3>
                  <p>Enter a few details and see a side-by-side Net ROI and annual dollar cost/savings comparison between Virdio Health and a competitor.</p>
                </div>
                <div className="inline-roi-inputs">
                  <div className="roi-field">
                    <label>Number of Employees</label>
                    <input type="number" placeholder="e.g. 5,000" value={roiInputs.employees} onChange={(e) => handleRoiInput("employees", e.target.value)} className={roiInvalid.employees ? "roi-input-error" : ""} />
                    <span className="roi-field-help">Total employees in your organization</span>
                  </div>
                  <div className="roi-field">
                    <label>Utilization Rate (%)</label>
                    <input type="number" placeholder="e.g. 8" value={roiInputs.util} onChange={(e) => handleRoiInput("util", e.target.value)} className={roiInvalid.util ? "roi-input-error" : ""} />
                    <span className="roi-field-help">% of employees who actively engage</span>
                  </div>
                  <div className="roi-field">
                    <label>Virdio Health Cost / Member</label>
                    <input type="text" value="$99" disabled className="roi-input-locked" />
                    <span className="roi-field-help">Fixed, all-in price per engaged member</span>
                  </div>
                  <div className="roi-field">
                    <label>Competitor Cost / Member</label>
                    <input type="number" placeholder="e.g. 1,000" value={roiInputs.comp} onChange={(e) => handleRoiInput("comp", e.target.value)} className={roiInvalid.comp ? "roi-input-error" : ""} />
                    <span className="roi-field-help">Cost per engaged member for the competing solution</span>
                  </div>
                  <div className="roi-field">
                    <label>Avg. Cost Reduction / Member</label>
                    <input type="number" placeholder="e.g. 2,500" value={roiInputs.savings} onChange={(e) => handleRoiInput("savings", e.target.value)} className={roiInvalid.savings ? "roi-input-error" : ""} />
                    <span className="roi-field-help">Many clinical studies show the avg. savings are $2500</span>
                  </div>
                  <div className="roi-field roi-field--btn">
                    <button type="button" className="btn-primary" onClick={calcInlineRoi}>
                      Calculate
                    </button>
                  </div>
                </div>

                {!roiResult && <p className="inline-roi-empty">{roiError || "Enter your details and click Calculate to see your comparison."}</p>}

                {roiResult && (
                  <div className="inline-roi-results">
                    <div className="inline-roi-subhead">Summary</div>

                    {/* ROI headline strip */}
                    <div className="inline-roi-grid">
                      <div className="inline-roi-col">
                        <div className="inline-roi-col-label">Virdio Health Net ROI</div>
                        <div className="inline-roi-val green">{fmtX(roiResult.roiV)}</div>
                      </div>
                      <div className="inline-roi-col">
                        <div className="inline-roi-col-label">Competitor Net ROI</div>
                        <div className="inline-roi-val soft">{fmtX(roiResult.roiC)}</div>
                      </div>
                      <div className="inline-roi-col">
                        <div className="inline-roi-col-label">Virdio Health Advantage</div>
                        <div className="inline-roi-val">{(roiResult.adv >= 0 ? "+" : "") + Math.round(roiResult.adv).toLocaleString() + "%"}</div>
                      </div>
                    </div>

                    {/* Hard dollar savings strip */}
                    <div className="inline-roi-grid" style={{ marginTop: "12px" }}>
                      <div className="inline-roi-col diff">
                        <div className="inline-roi-col-label">Annual Hard Dollar Savings: Virdio Health vs. Competitor</div>
                        <div className="inline-roi-val green">{fmt(roiResult.hardDiff)}</div>
                      </div>
                    </div>

                    <div className="inline-roi-subhead" style={{ marginTop: "24px" }}>
                      Details
                    </div>

                    {/* Hard Dollar Program Costs */}
                    <div className="ir-card">
                      <div className="ir-card-head">
                        <span className="ir-card-title">Hard Dollar Program Costs</span>
                        <span className="ir-card-desc">Engaged members × program cost per member</span>
                      </div>
                      <table className="ir-table">
                        <colgroup>
                          <col style={{ width: "44%" }} />
                          <col style={{ width: "28%" }} />
                          <col style={{ width: "28%" }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Metric</th>
                            <th className="ir-th-v">Virdio Health</th>
                            <th>Competitor</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="ir-l">Annual engaged members</td>
                            <td className="ir-v">{roiResult.engaged.toLocaleString()}</td>
                            <td>{roiResult.engaged.toLocaleString()}</td>
                          </tr>
                          <tr>
                            <td className="ir-l">Cost per engaged member</td>
                            <td className="ir-v">{fmt(roiResult.virdioCost)}</td>
                            <td>{fmt(roiResult.compCost)}</td>
                          </tr>
                          <tr>
                            <td className="ir-l">Annual total program cost</td>
                            <td className="ir-v">{fmt(roiResult.hardV)}</td>
                            <td>{fmt(roiResult.hardC)}</td>
                          </tr>
                          <tr className="ir-diff">
                            <td className="ir-l">Annual hard dollar savings vs. competitor</td>
                            <td className="ir-v">{fmt(roiResult.hardDiff)} savings</td>
                            <td>—</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Cost Avoidance */}
                    <div className="ir-card">
                      <div className="ir-card-head">
                        <span className="ir-card-title">Cost Avoidance Savings</span>
                        <span className="ir-card-desc">Engaged members × avg. cost reduction per member</span>
                      </div>
                      <table className="ir-table">
                        <colgroup>
                          <col style={{ width: "44%" }} />
                          <col style={{ width: "28%" }} />
                          <col style={{ width: "28%" }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Metric</th>
                            <th className="ir-th-v">Virdio Health</th>
                            <th>Competitor</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="ir-l">Avg. cost reduction per member</td>
                            <td className="ir-v">{fmt(roiResult.avgSavings)}</td>
                            <td>{fmt(roiResult.avgSavings)}</td>
                          </tr>
                          <tr>
                            <td className="ir-l">Annual cost avoidance savings</td>
                            <td className="ir-v">{fmt(roiResult.avoidance)}</td>
                            <td>{fmt(roiResult.avoidance)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* ROI */}
                    <div className="ir-card">
                      <div className="ir-card-head">
                        <span className="ir-card-title">Return on Investment</span>
                        <span className="ir-card-desc">(Cost avoidance savings − Program cost) ÷ Program cost</span>
                      </div>
                      <table className="ir-table">
                        <colgroup>
                          <col style={{ width: "44%" }} />
                          <col style={{ width: "28%" }} />
                          <col style={{ width: "28%" }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Metric</th>
                            <th className="ir-th-v">Virdio Health</th>
                            <th>Competitor</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="ir-l">Annual cost avoidance savings</td>
                            <td className="ir-v">{fmt(roiResult.avoidance)}</td>
                            <td>{fmt(roiResult.avoidance)}</td>
                          </tr>
                          <tr>
                            <td className="ir-l">Annual program cost</td>
                            <td className="ir-v">{fmt(roiResult.hardV)}</td>
                            <td>{fmt(roiResult.hardC)}</td>
                          </tr>
                          <tr>
                            <td className="ir-l">Annual net benefit</td>
                            <td className="ir-v">{fmt(roiResult.avoidance - roiResult.hardV)}</td>
                            <td>{fmt(roiResult.avoidance - roiResult.hardC)}</td>
                          </tr>
                          <tr className="ir-diff">
                            <td className="ir-l">Net ROI</td>
                            <td className="ir-v">{fmtX(roiResult.roiV)}</td>
                            <td>{fmtX(roiResult.roiC)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <p className="ir-formula">
                      <strong>How Net ROI is calculated:</strong> (Cost Avoidance Savings − Program Cost) ÷ Program Cost. Savings reflect reduced surgeries, imaging scans, injections, primary care and ER visits among engaged members.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="how-section" id="members">
          <div className="section-container">
            <div className="section-header">
              <p className="section-label">How It Works</p>
              <h2 className="section-title">
                From enrollment to relief, <span className="myra-accent"> without the friction.</span>
              </h2>
              <p className="section-desc">
                No app to download. <br /> No equipment to ship.
                <br /> No sensors to use. <br /> No appointments to schedule. <br /> No waiting rooms. <br /> No missed work. <br /> Myra handles everything else.
              </p>
            </div>
            <div className="how-grid">
              {howSteps.map((step) => (
                <div className="how-step" key={step.num}>
                  <div className="how-step-num">{step.num}</div>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="final-cta" id="demo">
          <div className="final-cta-inner">
            <h2>
              Built for the <span className="myra-accent">self-funded employer</span> <br /> who's tired of paying too much for less.
            </h2>
            <div className="final-cta-buttons">
              <Link to="/contact-us" className="btn-primary">
                Ask a Question or Schedule a Meeting
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

/* ===== ROI savings chart (ported from approved HTML SVG) ===== */
const RoiChart = () => (
  <svg className="roi-chart-svg" viewBox="0 0 640 560" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Cumulative savings chart, Virdio Health vs Competitor across four quarters">
    {/* gridlines */}
    {[60, 110, 160, 210, 260, 310, 360, 410, 460, 510].map((y, i) => (
      <line key={i} x1="120" y1={y} x2="600" y2={y} stroke="#e2e8f0" strokeWidth="1" />
    ))}
    {/* y axis labels */}
    {[
      { y: 64, t: "$1,000k" },
      { y: 114, t: "$900k" },
      { y: 164, t: "$800k" },
      { y: 214, t: "$700k" },
      { y: 264, t: "$600k" },
      { y: 314, t: "$500k" },
      { y: 364, t: "$400k" },
      { y: 414, t: "$300k" },
      { y: 464, t: "$200k" },
      { y: 514, t: "$100k" },
    ].map((l, i) => (
      <text key={i} x="112" y={l.y} textAnchor="end" fontSize="13" fill="#94a3b8" fontFamily="DM Sans, sans-serif">
        {l.t}
      </text>
    ))}
    {/* competitor line (coral) */}
    <polyline points="121,510 277,452 432,367 589,323" fill="none" stroke="#e07070" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
    {/* virdio line (teal) */}
    <polyline points="121,411 277,371 432,229 589,133" fill="none" stroke="#0d9488" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
    {/* x axis labels */}
    {[
      { x: 121, t: "Q1" },
      { x: 277, t: "Q2" },
      { x: 432, t: "Q3" },
      { x: 589, t: "Q4" },
    ].map((l, i) => (
      <text key={i} x={l.x} y="535" textAnchor="middle" fontSize="14" fill="#475569" fontFamily="DM Sans, sans-serif" fontWeight="600">
        {l.t}
      </text>
    ))}
  </svg>
);
